import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner } from "../../components/crm/Form";
import api, { formatApiError } from "../../lib/api";

export default function SolarSystemsList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const propertyId = params.get("property_id") || "";
  const initialQ = params.get("q") || "";
  const [list, setList] = useState([]);
  const [q, setQ] = useState(initialQ);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const filters = {};
      if (propertyId) filters.property_id = propertyId;
      if (q) filters.q = q;
      const { data } = await api.get("/solar-systems/", { params: filters });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    setParams(next, { replace: true });
    load();
  };

  return (
    <Layout title="Solar Systems" subtitle="CRM">
      <EntityHeader
        overline="installs"
        title="Solar Systems"
        subtitle="Every PV system Zion Solar tracks across all properties."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form
          onSubmit={submitSearch}
          className="relative flex-1 min-w-[240px] max-w-md"
        >
          <MagnifyingGlassIcon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search installer, utility…"
            data-testid="systems-search"
            className="w-full rounded-sm border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </form>
        {propertyId && (
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
            filter · property_id {propertyId.slice(0, 8)}
          </div>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="systems-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Year</th>
              <th className="text-left px-5 py-3 font-bold">Installer</th>
              <th className="text-left px-5 py-3 font-bold">Financing</th>
              <th className="text-left px-5 py-3 font-bold">Utility</th>
              <th className="text-left px-5 py-3 font-bold">Warranty</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && list.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                  data-testid="systems-empty"
                >
                  No solar systems yet.
                </td>
              </tr>
            )}
            {list.map((s) => (
              <tr
                key={s.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/solar-systems/${s.id}`)}
                data-testid={`system-row-${s.id}`}
              >
                <td className="px-5 py-3 font-data tabular text-slate-900">
                  {s.install_year}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {s.original_installer || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {s.financing_type}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {s.utility_company || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {s.warranty_expires || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
